import apiClient from "../../../utils/apiClient";
import profileLinkClient from "../../../utils/profileLinkClient";

/**
 * The student's own profile, through either door, one shape:
 *
 *   load()                         → the profile read (student, work history, documents, evaluation, options, institute)
 *   update(body)                   → the same read, after saving education / experience / companies
 *   submit()                       → the same read, after submitting for review
 *   documents.list / upload / remove   (useStudentDocuments takes this)
 *   agreement.pdfBytes / sendOtp / verifyOtp / sign
 *
 * `lmsProfileApi()` is the signed-in student on apiClient; `linkProfileApi(token)`
 * is the emailed link, every call a POST carrying the token in its body on the
 * public client (never apiClient — see utils/profileLinkClient).
 */

const multipart = { headers: { "Content-Type": "multipart/form-data" } };

const documentForm = ({ kind, companyId, label, file }, extra = {}) => {
  const body = new FormData();
  Object.entries(extra).forEach(([k, v]) => body.append(k, v));
  body.append("kind", kind);
  if (companyId) body.append("companyId", companyId);
  if (label) body.append("label", label);
  body.append("file", file, file.name);
  return body;
};

const progressOf = (onProgress) => (e) => {
  if (onProgress && e.total) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
};

export function lmsProfileApi() {
  const load = () => apiClient.get("/student-profile/me").then((r) => r.data);
  return {
    load,
    update: (body) => apiClient.put("/student-profile/me", body).then((r) => r.data),
    submit: () => apiClient.post("/student-profile/me/submit").then((r) => r.data),
    documents: {
      list: load,
      upload: (item, { onProgress } = {}) => apiClient
        .post("/student-profile/me/documents", documentForm(item), { ...multipart, onUploadProgress: progressOf(onProgress) })
        .then((r) => r.data.document),
      remove: (docId) => apiClient.delete(`/student-profile/me/documents/${docId}`),
      verify: null,
    },
    agreement: {
      pdfBytes: () => apiClient.get("/student-profile/me/agreement.pdf", { responseType: "arraybuffer" }).then((r) => new Uint8Array(r.data)),
      sendOtp: () => apiClient.post("/student-profile/me/agreement/otp").then((r) => r.data),
      verifyOtp: (code) => apiClient.post("/student-profile/me/agreement/otp/verify", { code }).then((r) => r.data),
      sign: (body) => apiClient.post("/student-profile/me/agreement/sign", body).then((r) => r.data),
    },
  };
}

export function linkProfileApi(token) {
  const post = (path, body = {}, config = {}) => profileLinkClient.post(`/public/student-profile${path}`, { token, ...body }, config);
  const load = () => post("/resolve").then((r) => r.data);
  return {
    load,
    update: (body) => post("/profile", body).then((r) => r.data),
    submit: () => post("/submit").then((r) => r.data),
    documents: {
      list: load,
      upload: (item, { onProgress } = {}) => profileLinkClient
        .post("/public/student-profile/documents", documentForm(item, { token }), { ...multipart, onUploadProgress: progressOf(onProgress) })
        .then((r) => r.data.document),
      remove: (docId) => post(`/documents/${docId}/delete`),
      verify: null,
    },
    agreement: {
      pdfBytes: () => post("/agreement/pdf", {}, { responseType: "arraybuffer" }).then((r) => new Uint8Array(r.data)),
      sendOtp: () => post("/agreement/otp").then((r) => r.data),
      verifyOtp: (code) => post("/agreement/otp/verify", { code }).then((r) => r.data),
      sign: (body) => post("/agreement/sign", body).then((r) => r.data),
    },
  };
}

/** A 401 from the link door: the token is dead and the page says so. */
export const isLinkRefusal = (err) => err?.response?.status === 401 && err?.response?.data?.code === "LINK_INVALID";
